import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { ApiError } from "../../lib/api";
import { useStudioSettings } from "../../stores/studio";
import { overlays, type Overlay } from "./api";

/**
 * The host's own images, and what is currently on the frame.
 *
 * ## Two lists, because they are two different things
 *
 * The library is what this host has uploaded - it belongs to them and outlives
 * any arrangement. The layer list is what is on *this* scene right now. One
 * picture can be in the library and on the frame twice, or in the library and
 * on nothing at all, and collapsing the two would make both of those states
 * impossible to express.
 *
 * It also means removing a picture from the frame does not delete it, which is
 * the behaviour anybody would expect and the opposite of what a single list
 * would do.
 */
export function OverlayPanel() {
  const addImage = useStudioSettings((s) => s.addImage);

  const client = useQueryClient();
  const picker = useRef<HTMLInputElement | null>(null);

  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);

  const library = useQuery({
    queryKey: ["studio-overlays"],
    queryFn: overlays.list,
  });

  const add = async (file: File) => {
    setBusy(true);
    setProblem(null);
    setProgress(0);

    try {
      const overlay = await overlays.upload(file, {
        onProgress: setProgress,
      });

      await client.invalidateQueries({ queryKey: ["studio-overlays"] });

      /*
       * Placed straight onto the frame.
       *
       * Somebody who has just picked a file wants to see it, and making them
       * find it in a grid and click it again is a step that exists only because
       * the upload and the placing happen to be two operations underneath.
       */
      if (overlay) {
        addImage(overlay);
      } else {
        // The worker has not finished within the poll window. The upload landed
        // and it will appear in the library on its own, so this is a status
        // rather than a failure.
        setProblem("still processing, it will appear in a moment");
      }
    } catch (cause) {
      setProblem(
        cause instanceof ApiError
          ? cause.message
          : "that image could not be uploaded",
      );
    } finally {
      setBusy(false);
    }
  };

  const forget = async (id: number) => {
    try {
      await overlays.remove(id);
      await client.invalidateQueries({ queryKey: ["studio-overlays"] });
    } catch {
      // Nothing useful to say: the list will show whether it went.
    }
  };

  return (
    <div className="flex flex-col gap-3" data-studio-overlays>
      {/*
        The library. Clicking one puts it on the frame; a picture can be placed
        more than once, which is why this is not a set of toggles.
      */}
      {library.data && library.data.overlays.length > 0 && (
        <div className="grid grid-cols-4 gap-1.5">
          {library.data.overlays.map((overlay) => (
            <Thumb
              key={overlay.id}
              overlay={overlay}
              onPlace={() => addImage(overlay)}
              onForget={() => void forget(overlay.id)}
            />
          ))}
        </div>
      )}

      <input
        ref={picker}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        data-studio-image-input
        onChange={(event) => {
          const file = event.target.files?.[0];

          // Cleared so picking the same file twice in a row fires `change`
          // again - otherwise the second attempt after an error does nothing.
          event.target.value = "";

          if (file) void add(file);
        }}
      />

      <button
        type="button"
        onClick={() => picker.current?.click()}
        disabled={busy}
        data-studio-add-image
        className="rounded-[var(--radius-control)] border border-line px-3 py-2 text-[12px] text-soft transition-colors hover:border-line-lit hover:text-ink disabled:opacity-60"
      >
        {busy ? `Uploading… ${Math.round(progress)}%` : "Add an image"}
      </button>

      {problem && (
        <p
          className="font-mono text-[10px] leading-relaxed text-hot-text"
          data-studio-image-problem
        >
          {problem}
        </p>
      )}

      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        png keeps its transparency, so logos and frames sit over the picture
        without a box around them
      </p>
    </div>
  );
}

function Thumb({
  overlay,
  onPlace,
  onForget,
}: {
  overlay: Overlay;
  onPlace: () => void;
  onForget: () => void;
}) {
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onPlace}
        title={`${overlay.label} — put it on the frame`}
        data-studio-overlay={overlay.id}
        className="block aspect-square w-full overflow-hidden rounded-[var(--radius-control)] border border-line p-1 transition-colors hover:border-accent"
      >
        {/*
          A checkerboard, because most of these have transparent backgrounds and
          a transparent picture on a dark panel is indistinguishable from an
          empty box.
        */}
        <span
          className="flex h-full w-full items-center justify-center rounded-[3px]"
          style={{
            backgroundImage:
              "repeating-conic-gradient(#2a2a3a 0% 25%, #1c1c28 0% 50%)",
            backgroundSize: "10px 10px",
          }}
        >
          <img
            src={overlay.url}
            alt={overlay.label}
            className="max-h-full max-w-full object-contain"
          />
        </span>
      </button>

      <button
        type="button"
        onClick={onForget}
        aria-label={`Delete ${overlay.label}`}
        data-studio-overlay-delete={overlay.id}
        className="absolute -right-1 -top-1 hidden h-5 w-5 items-center justify-center rounded-full bg-hot text-[11px] font-bold text-white group-hover:flex"
      >
        ×
      </button>
    </div>
  );
}
